/**
 * Workstream → accent mapping for the APQC process-catalog presentation (the
 * coloured "L1"/"L2" level tiles, card borders and group washes in the browse
 * picker).
 *
 * `alternate-*` are identical in light and dark mode while `--foreground`
 * flips, so anything sitting *on* an accent fill must use
 * `text-primary-foreground` (dark in both themes).
 *
 * Keyed by the workstream *display string*, because the process catalog and the
 * agent catalog spell some workstreams differently ("Procure-to-Pay" vs
 * "Procure-to-Pay / AP"). Anything unmapped falls back to a stable hash so a new
 * workstream still gets a colour instead of no colour.
 */

export type TCatalogAccent =
  | 'blue'
  | 'mint'
  | 'violet'
  | 'amber'
  | 'cyan'
  | 'rose'
  | 'olive';

/** The class set an accent contributes. */
export interface ICatalogAccentClasses {
  /** Solid fill for the L1 level tile. Pair with `text-primary-foreground`. */
  tile: string;
  /** Softer fill for the smaller L2 level tile. */
  softTile: string;
  /** Color for the 2px border of the expanded L1 card. */
  cardBorder: string;
  /** Border for the expanded L2 group's own rounded container. */
  groupBorder: string;
  /**
   * Background wash for an expanded container (the open L1 card, the open L2
   * group). Lives on the *container*.
   */
  wash: string;
  /**
   * Hover tint for an interactive row: the *same* hue and the *same* alpha as
   * {@link wash}, so hovering a row previews exactly the colour selecting it
   * will give.
   */
  hoverWash: string;
}

export const CATALOG_ACCENT: Record<TCatalogAccent, ICatalogAccentClasses> = {
  // blue
  blue: {
    tile: 'bg-alternate-7',
    softTile: 'bg-alternate-7/30',
    cardBorder: 'border-alternate-7',
    groupBorder: 'border-alternate-7/40',
    wash: 'bg-alternate-7/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-7/25 dark:not-disabled:hover:bg-alternate-7/25',
  },
  // green
  mint: {
    tile: 'bg-alternate-5',
    softTile: 'bg-alternate-5/30',
    cardBorder: 'border-alternate-5',
    groupBorder: 'border-alternate-5/40',
    wash: 'bg-alternate-5/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-5/25 dark:not-disabled:hover:bg-alternate-5/25',
  },
  // purple
  violet: {
    tile: 'bg-alternate-8',
    softTile: 'bg-alternate-8/30',
    cardBorder: 'border-alternate-8',
    groupBorder: 'border-alternate-8/40',
    wash: 'bg-alternate-8/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-8/25 dark:not-disabled:hover:bg-alternate-8/25',
  },
  // orange
  amber: {
    tile: 'bg-alternate-3',
    softTile: 'bg-alternate-3/30',
    cardBorder: 'border-alternate-3',
    groupBorder: 'border-alternate-3/40',
    wash: 'bg-alternate-3/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-3/25 dark:not-disabled:hover:bg-alternate-3/25',
  },
  // teal
  cyan: {
    tile: 'bg-alternate-6',
    softTile: 'bg-alternate-6/30',
    cardBorder: 'border-alternate-6',
    groupBorder: 'border-alternate-6/40',
    wash: 'bg-alternate-6/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-6/25 dark:not-disabled:hover:bg-alternate-6/25',
  },
  // pink
  rose: {
    tile: 'bg-alternate-9',
    softTile: 'bg-alternate-9/30',
    cardBorder: 'border-alternate-9',
    groupBorder: 'border-alternate-9/40',
    wash: 'bg-alternate-9/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-9/25 dark:not-disabled:hover:bg-alternate-9/25',
  },
  // yellow
  olive: {
    tile: 'bg-alternate-4',
    softTile: 'bg-alternate-4/30',
    cardBorder: 'border-alternate-4',
    groupBorder: 'border-alternate-4/40',
    wash: 'bg-alternate-4/25',
    hoverWash:
      'not-disabled:hover:bg-alternate-4/25 dark:not-disabled:hover:bg-alternate-4/25',
  },
};

/**
 * Known workstreams, in both the process-catalog and agent-catalog spellings.
 * The accents match `WORKSTREAM_META` so a workstream keeps one colour across
 * the Fleet lens, the workstream cockpit, and this picker.
 */
const KNOWN_ACCENTS: Record<string, TCatalogAccent> = {
  'Record-to-Report': 'violet',
  'Procure-to-Pay': 'blue',
  'Procure-to-Pay / AP': 'blue',
  'Order-to-Cash': 'mint',
  'Order-to-Cash / AR': 'mint',
  Treasury: 'cyan',
  'Treasury & Cash Mgmt': 'cyan',
  'FP&A': 'rose',
  'Financial Planning & Analysis': 'rose',
  Tax: 'amber',
  'Tax Compliance': 'amber',
  'Controls & Audit': 'olive',
  Payroll: 'olive',
  'Cross-Functional': 'blue',
  'BIAN · Retail Banking and Consumer': 'blue',
  'BIAN Retail': 'blue',
  'BIAN · Lending': 'mint',
  'BIAN Loans': 'mint',
  'BIAN · Payments': 'cyan',
  'BIAN Payments': 'cyan',
  'BIAN · Card Products': 'violet',
  'BIAN Cards': 'violet',
  'BIAN · Bank Relations': 'rose',
  'BIAN Relations': 'rose',
  'BIAN · Business Development': 'amber',
  'BIAN Business': 'amber',
  'BIAN · Channels': 'olive',
  'BIAN Channels': 'olive',
  'BIAN · Corporate Banking Products': 'blue',
  'BIAN Corporate': 'blue',
  'BIAN · Corporate Finance': 'mint',
  'BIAN Corp Fin': 'mint',
  'BIAN · Wealth': 'cyan',
  'BIAN Wealth': 'cyan',
  'BIAN · Product and Price': 'violet',
  'BIAN Products': 'violet',
  'Finance · Record-to-Report': 'violet',
  'Finance R2R': 'violet',
  'Finance · Procure-to-Pay / AP': 'blue',
  'Finance P2P': 'blue',
  'Finance · Order-to-Cash / AR': 'mint',
  'Finance O2C': 'mint',
  'Finance · Treasury & Cash Mgmt': 'cyan',
  'Finance Treasury': 'cyan',
  'Finance · Financial Planning & Analysis': 'rose',
  'Finance FP&A': 'rose',
  'Finance · Tax Compliance': 'amber',
  'Finance Tax': 'amber',
  'Finance · Payroll': 'olive',
  'Finance Payroll': 'olive',
  General: 'blue',
};

/** Fallback cycle for unmapped workstreams. */
const ACCENT_CYCLE: TCatalogAccent[] = [
  'violet',
  'blue',
  'mint',
  'cyan',
  'rose',
  'amber',
  'olive',
];

/** Resolve a catalog workstream display string to a stable accent. */
export function catalogWorkstreamAccent(workstream: string): TCatalogAccent {
  const known = KNOWN_ACCENTS[workstream];
  if (known) return known;
  let hash = 0;
  for (const char of workstream) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return ACCENT_CYCLE[hash % ACCENT_CYCLE.length];
}

/**
 * The workstream's accent as a raw CSS custom property, e.g. `--alternate-7`.
 */
export function catalogWorkstreamAccentVar(workstream: string): string {
  // Every accent's tile is `bg-alternate-N`, so the token name comes off it
  // rather than being a second table to keep in sync.
  return `--${CATALOG_ACCENT[catalogWorkstreamAccent(workstream)].tile.replace('bg-', '')}`;
}

/** The full accent class set for a workstream. */
export function catalogWorkstreamAccentClasses(
  workstream: string,
): ICatalogAccentClasses {
  return CATALOG_ACCENT[catalogWorkstreamAccent(workstream)];
}
